import {
  IngestionLabel,
  ingestionLabel,
  IngestionStatus,
} from "@/shared/domain/files";
import {
  type PendingIngestionFile,
  type SessionIngestionStatus,
  skippedFileCount,
} from "@/shared/domain/sessions";

/** The id of the Details tab's ingestion panel, which links scroll to. */
export const SESSION_INGESTION_ANCHOR = "session-ingestion";

/** The Details tab, scrolled to its ingestion panel. */
export function sessionIngestionPanelLink(sessionId: string) {
  return {
    pathname: `/sessions/${encodeURIComponent(sessionId)}/details`,
    hash: SESSION_INGESTION_ANCHOR,
  };
}

export const PENDING_FILE_INGESTION_STATUS: Record<
  PendingIngestionFile["ingestion_status"],
  IngestionStatus
> = {
  not_ingested: IngestionStatus.NotIngested,
  partly_ingested: IngestionStatus.PartlyIngested,
  ingested: IngestionStatus.Ingested,
};

/** Whether the pending file is labelled "Ingestion failed". */
export function ingestionRunFailed(file: PendingIngestionFile): boolean {
  return (
    ingestionLabel(
      {
        ingestion_status: PENDING_FILE_INGESTION_STATUS[file.ingestion_status],
        ingestable: true,
      },
      { latestRun: file.last_run },
    ) === IngestionLabel.IngestionFailed
  );
}

/** "3 files", "1 file". */
export function fileCount(count: number): string {
  return `${count} ${count === 1 ? "file" : "files"}`;
}

/**
 * The session's files that are not pending, by where their ingestion stands:
 * "3 ingested, 2 stored as-is, 1 not waited for". Null when it has none.
 */
export function ingestionCountsSentence(
  status: SessionIngestionStatus,
): string | null {
  const skipped = skippedFileCount(status);
  const parts = [
    status.ingested_file_count > 0 && `${status.ingested_file_count} ingested`,
    status.not_ingestable_file_count > 0 &&
      `${status.not_ingestable_file_count} stored as-is`,
    skipped > 0 && `${skipped} not waited for`,
  ].filter((part): part is string => part !== false);
  if (parts.length === 0) {
    return null;
  }
  return `${parts.join(", ")}.`;
}
